import { Box, keyframes, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { Container, Heading, Text } from '../atomic';

/**
 * @typedef {Object} Integration
 * @property {string} id - Unique identifier
 * @property {string} name - Integration name
 * @property {string} [logoSrc] - Logo image source
 * @property {string} category - Integration category
 */

/**
 * @typedef {Object} IntegrationsSectionProps
 * @property {string} [sectionId] - Section ID for navigation
 * @property {string} [headline] - Section headline
 * @property {string} [subheadline] - Section subheadline
 * @property {Integration[]} [integrations] - Array of integrations
 */

/** Marquee animation keyframes */
const scrollLeft = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;

const scrollRight = keyframes`
  from { transform: translateX(-50%); }
  to { transform: translateX(0); }
`;

/**
 * Integrations Section
 * Inspired by Stripe + Lucky Orange
 *
 * @param {IntegrationsSectionProps} props - Component props
 * @returns {JSX.Element} Integrations section
 */
function IntegrationsSection({
  sectionId = 'integrations',
  headline = 'Works With Your Existing Stack',
  subheadline = 'Direct AWS integration. Export to any ERP. No workflow disruption.',
  integrations = [
    { id: 'aws', name: 'AWS', category: 'Cloud' },
    { id: 'quickbooks', name: 'QuickBooks', category: 'ERP' },
    { id: 'netsuite', name: 'NetSuite', category: 'ERP' },
    { id: 'sage', name: 'Sage', category: 'ERP' },
    { id: 'sap', name: 'SAP', category: 'ERP' },
    { id: 'excel', name: 'Excel', category: 'Export' },
    { id: 'xero', name: 'Xero', category: 'ERP' },
    { id: 'freshbooks', name: 'FreshBooks', category: 'ERP' },
  ],
}) {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const theme = useTheme();

  // Split integrations into two rows
  const row1 = integrations.slice(0, Math.ceil(integrations.length / 2));
  const row2 = integrations.slice(Math.ceil(integrations.length / 2));

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  /**
   * Render an integration card
   * @param {Integration} integration - Integration data
   * @param {number} index - Integration index
   * @param {string} suffix - Key suffix
   * @returns {JSX.Element} Integration card
   */
  const renderIntegrationCard = (integration, index, suffix = '') => (
    <Box
      key={`${integration.id}-${suffix}-${index}`}
      sx={{
        minWidth: '160px',
        height: '80px',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
        border: '1px solid rgba(0, 0, 0, 0.06)',
        transition: 'all 0.3s ease',
        cursor: 'default',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 24px rgba(125, 144, 26, 0.12)',
          borderColor: 'rgba(125, 144, 26, 0.3)',
        },
      }}
    >
      {integration.logoSrc
        ? <Box
            component="img"
            src={integration.logoSrc}
            alt={`${integration.name} logo`}
            sx={{
              maxHeight: '36px',
              maxWidth: '100px',
            }}
          />
        : <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: theme.palette.primary.main,
                fontSize: '1rem',
              }}
            >
              {integration.name}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: '#6c757d',
                fontSize: '0.75rem',
              }}
            >
              {integration.category}
            </Typography>
          </Box>}
    </Box>
  );

  /**
   * Render a marquee row
   * @param {Integration[]} items - Row items
   * @param {string} direction - Animation direction ('left' | 'right')
   * @returns {JSX.Element} Marquee row
   */
  const renderMarqueeRow = (items, direction = 'left') => (
    <Box
      sx={{
        overflow: 'hidden',
        py: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          animation: `${direction === 'left' ? scrollLeft : scrollRight} 25s linear infinite`,
          willChange: 'transform',
          '&:hover': {
            animationPlayState: 'paused',
          },
        }}
      >
        {/* First set of items */}
        {items.map((item, index) =>
          renderIntegrationCard(item, index, 'first')
        )}
        {/* Duplicate set for seamless infinite scroll */}
        {items.map((item, index) =>
          renderIntegrationCard(item, index, 'second')
        )}
      </Box>
    </Box>
  );

  return (
    <Box
      component="section"
      id={sectionId}
      ref={sectionRef}
      aria-label="Integrations"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: '#f8f9fa',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box
          sx={{
            textAlign: 'center',
            mb: { xs: 5, md: 6 },
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s ease',
          }}
        >
          <Heading
            variant="h2"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              fontWeight: 700,
              color: theme.palette.primary.main,
              mb: 2,
            }}
          >
            {headline}
          </Heading>
          <Text
            sx={{
              fontSize: { xs: '1rem', md: '1.2rem' },
              color: '#6c757d',
              maxWidth: '550px',
              mx: 'auto',
              lineHeight: 1.7,
            }}
          >
            {subheadline}
          </Text>
        </Box>
      </Container>

      {/* Integration Marquees */}
      <Box
        sx={{
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.6s ease 0.3s',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {renderMarqueeRow(row1, 'left')}
        {renderMarqueeRow(row2, 'right')}
      </Box>

      <Container maxWidth="lg">
        {/* Additional Info */}
        <Box
          sx={{
            textAlign: 'center',
            mt: 5,
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.6s ease 0.6s',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: '#6c757d',
              fontSize: '0.95rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box
              component="span"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  width: '8px',
                  height: '8px',
                  backgroundColor: theme.palette.secondary.main,
                  clipPath: 'polygon(0 0, 100% 0, 0 100%)',
                }}
              />
              SOC 2 Compliant
            </Box>
            <Box component="span" sx={{ color: '#e0e0e0' }}>
              |
            </Box>
            <Box
              component="span"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  width: '8px',
                  height: '8px',
                  backgroundColor: theme.palette.secondary.main,
                  clipPath: 'polygon(0 0, 100% 0, 0 100%)',
                }}
              />
              Read-only Access
            </Box>
            <Box component="span" sx={{ color: '#e0e0e0' }}>
              |
            </Box>
            <Box
              component="span"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  width: '8px',
                  height: '8px',
                  backgroundColor: theme.palette.secondary.main,
                  clipPath: 'polygon(0 0, 100% 0, 0 100%)',
                }}
              />
              No Agent Required
            </Box>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default IntegrationsSection;
